import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  Check,
  ChevronDown,
  CircleCheck,
  FilePenLine,
  Pencil,
  Send,
  Trash2,
  Undo2,
} from 'lucide-react';

import {
  approveMiscPurchaseOrder,
  cancelDraftMiscPurchaseOrder,
  closeMiscPurchaseOrder,
  rejectMiscPurchaseOrder,
  submitMiscPurchaseOrder,
  terminateMiscPurchaseOrder,
  updateMiscPurchaseOrderRemark,
} from '#/api/srm/miscPurchaseOrder';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import { DialogFooter } from '#/modules/commonComponents/dialog/DialogPrimitives';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/modules/commonComponents/dropdown-menu';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import {
  pickAvailableWorkflowExposedIds,
  resolveLgDesktopLayout,
  resolveMdDesktopLayout,
  resolveMobileLayout,
} from '#/modules/imsMgmt/receipts/shared/documentCommandBarLayout';
import { useCan } from '#/modules/infra/auth/useCan';
import {
  miscPurchaseOrderDetailQueryKey,
  miscPurchaseOrderListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

import {
  getMiscPurchaseOrderApiErrorMessage,
  isMiscPurchaseOrderVersionConflict,
} from '../helper/apiError';
import {
  canAmendMiscPurchaseOrder,
  canApproveMiscPurchaseOrder,
  canCancelDraftMiscPurchaseOrder,
  canCloseMiscPurchaseOrder,
  canTerminateMiscPurchaseOrder,
  canEditMiscPurchaseOrderDraft,
  canRejectMiscPurchaseOrder,
  canSubmitMiscPurchaseOrder,
} from '../helper/miscPurchaseOrderStatus';
import {
  PURCHASE_AMENDMENT_STATUS,
  findOpenPurchaseAmendment,
} from '#/modules/srmMgmt/purchaseOrder/helper/purchaseAmendmentStatus';
import { pickMiscPurchaseOrderUpdatedAt } from '../schemas/miscPurchaseOrderSchema';

const WORKFLOW_EXPOSED_ORDER = ['submit', 'edit', 'approve', 'reject', 'close'];

const ACTION_GROUPS = [
  { key: 'content', label: '單據內容', ids: ['edit', 'editRemark', 'createAmendment'] },
  {
    key: 'status',
    label: '單據狀態',
    ids: ['submit', 'approve', 'reject', 'close', 'terminate', 'cancelDraft'],
  },
];

const ACTION_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  to: PropTypes.string,
  onSelect: PropTypes.func,
  destructive: PropTypes.bool,
  disabled: PropTypes.bool,
  available: PropTypes.bool,
  showExposedIcon: PropTypes.bool,
  exposedVariant: PropTypes.string,
  exposedClassName: PropTypes.string,
});

function getExposedActionIds(actionMap) {
  return pickAvailableWorkflowExposedIds(actionMap, WORKFLOW_EXPOSED_ORDER);
}

function runAfterMenuClose(callback) {
  window.setTimeout(() => {
    callback?.();
  }, 0);
}

function ExposedActionButton({ action, isPending }) {
  const Icon = action.icon;
  const disabled = Boolean(isPending || action.disabled);
  const iconNode = action.showExposedIcon && Icon
    ? <Icon className="size-3.5" aria-hidden />
    : null;

  if (action.to && !disabled) {
    return (
      <Button
        asChild
        variant={action.exposedVariant || 'default'}
        className={cn(action.exposedClassName)}
      >
        <Link to={action.to} aria-label={action.label}>
          {iconNode}
          {action.label}
        </Link>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant={action.destructive ? 'destructive' : (action.exposedVariant || 'default')}
      className={cn(
        action.destructive && DESTRUCTIVE_SOLID_BUTTON_CLASS,
        action.exposedClassName,
      )}
      disabled={disabled}
      onClick={() => action.onSelect?.()}
    >
      {iconNode}
      {action.label}
    </Button>
  );
}

ExposedActionButton.propTypes = {
  action: ACTION_SHAPE.isRequired,
  isPending: PropTypes.bool,
};

function ActionMenuItem({ action, isPending }) {
  const Icon = action.icon;
  const disabled = Boolean(isPending || action.disabled);
  const iconNode = Icon ? <Icon className="size-4" aria-hidden /> : null;

  if (action.to && !disabled) {
    return (
      <DropdownMenuItem
        asChild
        variant={action.destructive ? 'destructive' : 'default'}
      >
        <Link to={action.to}>
          {iconNode}
          {action.label}
        </Link>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem
      variant={action.destructive ? 'destructive' : 'default'}
      disabled={disabled}
      onSelect={() => {
        if (disabled) {
          return;
        }
        runAfterMenuClose(action.onSelect);
      }}
    >
      {iconNode}
      {action.label}
    </DropdownMenuItem>
  );
}

ActionMenuItem.propTypes = {
  action: ACTION_SHAPE.isRequired,
  isPending: PropTypes.bool,
};

function ActionsMenu({
  triggerLabel,
  triggerAriaLabel,
  groups,
  isPending,
}) {
  if (!groups.length) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={isPending}
          aria-label={triggerAriaLabel || triggerLabel}
        >
          {triggerLabel}
          <ChevronDown className="size-3.5" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-auto min-w-44">
        {groups.map((group, index) => (
          <Fragment key={group.key}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
            {group.items.map((action) => (
              <ActionMenuItem key={action.id} action={action} isPending={isPending} />
            ))}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

ActionsMenu.propTypes = {
  triggerLabel: PropTypes.string.isRequired,
  triggerAriaLabel: PropTypes.string,
  groups: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(ACTION_SHAPE).isRequired,
  })).isRequired,
  isPending: PropTypes.bool,
};

const MiscPurchaseOrderCommandBar = ({ detail }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { confirm, confirmDialog } = useConfirmDialog();
  const canUpdate = useCan(PERM.MISC_PURCHASE_ORDER_UPDATE);
  const canReview = useCan(PERM.MISC_PURCHASE_ORDER_REVIEW);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [remarkOpen, setRemarkOpen] = useState(false);
  const [remark, setRemark] = useState('');

  const id = detail?.id;
  const status = detail?.status;
  // 同一採購單同時最多一張在途異動；在途期間不可再建或結案
  const openAmendment = findOpenPurchaseAmendment(detail?.amendments);
  const openAmendmentIsDraft = openAmendment?.status === PURCHASE_AMENDMENT_STATUS.DRAFT;
  const updatedAt = pickMiscPurchaseOrderUpdatedAt(detail);

  async function refresh() {
    await queryClient.invalidateQueries({
      queryKey: miscPurchaseOrderDetailQueryKey(id),
    });
    await queryClient.invalidateQueries({
      queryKey: miscPurchaseOrderListQueryKeyPrefix(),
    });
  }

  const commandMutation = useMutation({
    mutationFn: ({ action, payload }) => {
      const body = { updatedAt, ...payload };
      if (action === 'submit') {
        return submitMiscPurchaseOrder(id, body);
      }
      if (action === 'approve') {
        return approveMiscPurchaseOrder(id, body);
      }
      if (action === 'reject') {
        return rejectMiscPurchaseOrder(id, body);
      }
      if (action === 'close') {
        return closeMiscPurchaseOrder(id, body);
      }
      if (action === 'terminate') {
        return terminateMiscPurchaseOrder(id, body);
      }
      if (action === 'cancel') {
        return cancelDraftMiscPurchaseOrder(id, body);
      }
      return updateMiscPurchaseOrderRemark(id, body);
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      setRejectOpen(false);
      setCloseOpen(false);
      setTerminateOpen(false);
      setRemarkOpen(false);
      if (variables.action === 'cancel') {
        // 草稿已實體刪除；detail 會 404，直接回列表
        await queryClient.invalidateQueries({
          queryKey: miscPurchaseOrderListQueryKeyPrefix(),
        });
        navigate(ROUTES.miscPurchaseOrder);
        return;
      }
      await refresh();
    },
    onError: (error) => {
      toast.error(getMiscPurchaseOrderApiErrorMessage(error, '操作失敗'));
      if (isMiscPurchaseOrderVersionConflict(error)) {
        refresh();
      }
    },
  });

  const pending = commandMutation.isPending;

  async function handleSubmit() {
    const ok = await confirm({
      title: '提交非庫存採購單',
      message: '提交後將進入審核，確定提交？',
      confirmText: '提交',
    });
    if (ok) {
      commandMutation.mutate({ action: 'submit' });
    }
  }

  async function handleCancel() {
    const ok = await confirm({
      title: '刪除草稿',
      message: '草稿將永久刪除，無法復原，確定刪除？',
      confirmText: '刪除草稿',
      destructive: true,
    });
    if (ok) {
      commandMutation.mutate({ action: 'cancel' });
    }
  }

  function openRemark() {
    setRemark(detail?.remark ?? '');
    setRemarkOpen(true);
  }

  const actionMap = {
    edit: {
      id: 'edit',
      label: '編輯',
      icon: Pencil,
      to: ROUTES.miscPurchaseOrderEdit(id),
      available: canUpdate && canEditMiscPurchaseOrderDraft(status),
      showExposedIcon: true,
      exposedVariant: 'default',
      exposedClassName: 'document-detail-edit-action',
    },
    editRemark: {
      id: 'editRemark',
      label: '編輯備註',
      icon: FilePenLine,
      available: canUpdate,
      showExposedIcon: true,
      exposedVariant: 'outline',
      onSelect: openRemark,
    },
    createAmendment: {
      id: 'createAmendment',
      label: openAmendmentIsDraft ? '編輯異動草稿' : '新增異動',
      icon: FilePenLine,
      to: openAmendmentIsDraft
        ? ROUTES.miscPurchaseOrderAmendmentEdit(id, openAmendment.id)
        : ROUTES.miscPurchaseOrderAmendmentCreate(id),
      available: canUpdate && canAmendMiscPurchaseOrder(status)
        && (!openAmendment || openAmendmentIsDraft),
      showExposedIcon: true,
      exposedVariant: 'outline',
    },
    submit: {
      id: 'submit',
      label: '提交',
      icon: Send,
      available: canUpdate && canSubmitMiscPurchaseOrder(status),
      onSelect: handleSubmit,
    },
    approve: {
      id: 'approve',
      label: '核准',
      icon: Check,
      available: canReview && canApproveMiscPurchaseOrder(status),
      onSelect: () => commandMutation.mutate({ action: 'approve' }),
    },
    reject: {
      id: 'reject',
      label: '退回',
      icon: Undo2,
      available: canReview && canRejectMiscPurchaseOrder(status),
      destructive: true,
      onSelect: () => setRejectOpen(true),
    },
    close: {
      id: 'close',
      label: '結案',
      icon: CircleCheck,
      // be doc：close 需 srm.misc_purchase_order.review
      available: canReview && !openAmendment && canCloseMiscPurchaseOrder(status),
      onSelect: () => setCloseOpen(true),
    },
    terminate: {
      id: 'terminate',
      label: '中止',
      icon: Ban,
      available: canReview && !openAmendment && canTerminateMiscPurchaseOrder(status),
      destructive: true,
      onSelect: () => setTerminateOpen(true),
    },
    cancelDraft: {
      id: 'cancelDraft',
      label: '刪除草稿',
      icon: Trash2,
      available: canUpdate && canCancelDraftMiscPurchaseOrder(status),
      destructive: true,
      onSelect: handleCancel,
    },
  };

  const mobileLayout = resolveMobileLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
  });
  const mdLayout = resolveMdDesktopLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
    getExposedActionIds,
  });
  const lgLayout = resolveLgDesktopLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
    getExposedActionIds,
  });

  function renderLayoutRow(
    layout,
    { triggerLabel = '更多', triggerAriaLabel = '更多操作' } = {},
  ) {
    return (
      <>
        {layout.flatActions.map((action) => (
          <ExposedActionButton key={action.id} action={action} isPending={pending} />
        ))}
        {layout.showMoreMenu ? (
          <ActionsMenu
            triggerLabel={triggerLabel}
            triggerAriaLabel={triggerAriaLabel}
            groups={layout.menuGroups}
            isPending={pending}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      {confirmDialog}
      <div className="flex items-center justify-end gap-2">
        <div className="flex items-center gap-2 md:hidden">
          {renderLayoutRow(mobileLayout, {
            triggerLabel: '操作',
            triggerAriaLabel: '單據操作',
          })}
        </div>
        <div className="hidden items-center gap-2 md:flex lg:hidden">
          {renderLayoutRow(mdLayout)}
        </div>
        <div className="hidden items-center gap-2 lg:flex">
          {renderLayoutRow(lgLayout)}
        </div>
      </div>

      <DocumentRejectReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="退回非庫存採購單"
        description="退回後回到草稿。"
        isPending={pending}
        onConfirm={(rejectReason) => {
          commandMutation.mutate({ action: 'reject', payload: { rejectReason } });
        }}
      />
      <DocumentRejectReasonDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        title="結案非庫存採購單"
        description="結案由使用者決定，不檢查收貨。"
        confirmText="結案"
        reasonLabel="結案原因"
        emptyErrorMessage="請填寫結案原因"
        isPending={pending}
        onConfirm={(closeReason) => {
          commandMutation.mutate({ action: 'close', payload: { closeReason } });
        }}
      />
      <DocumentRejectReasonDialog
        open={terminateOpen}
        onOpenChange={setTerminateOpen}
        title="中止非庫存採購單"
        description="中止後不可再異動或結案。"
        confirmText="中止"
        reasonLabel="中止原因"
        emptyErrorMessage="請填寫中止原因"
        isPending={pending}
        onConfirm={(terminateReason) => {
          commandMutation.mutate({ action: 'terminate', payload: { terminateReason } });
        }}
      />
      <AppDialog
        open={remarkOpen}
        onOpenChange={setRemarkOpen}
        title="更新備註"
        headerVariant="muted"
        footer={(
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRemarkOpen(false)}>
              取消
            </Button>
            <Button
              type="button"
              disabled={pending}
              onClick={() => commandMutation.mutate({
                action: 'remark',
                payload: { remark },
              })}
            >
              儲存
            </Button>
          </DialogFooter>
        )}
      >
        <FloatingTextarea
          id="miscPurchaseOrderRemark"
          label="備註"
          value={remark}
          onChange={(event) => setRemark(event.target.value)}
        />
      </AppDialog>
    </>
  );
};

MiscPurchaseOrderCommandBar.propTypes = {
  detail: PropTypes.object.isRequired,
};

export default MiscPurchaseOrderCommandBar;
